/**
 * TechnologyCapability Validation Schemas
 */

import { z } from 'zod';
import { nameField, optionalBoundedTextOrEmpty } from './shared';

/**
 * Common validation rules for TechnologyCapability entities
 */
export const technologyCapabilityValidationRules = {
  /** TechnologyCapability name validation (1-200 characters) */
  name: nameField(200, 'Name is required', 'Name must be less than 200 characters'),

  /** Description validation (optional, max 2000 characters) */
  description: optionalBoundedTextOrEmpty(2000, 'Description must be less than 2000 characters'),

  /** Domain ID validation (optional string) */
  domainId: optionalBoundedTextOrEmpty(200, 'Domain ID must be less than 200 characters'),

  /** Notes validation (optional, max 5000 characters) */
  notes: optionalBoundedTextOrEmpty(5000, 'Notes must be less than 5000 characters'),
};

/**
 * Full TechnologyCapability create form schema
 */
export const createTechnologyCapabilitySchema = z.object({
  name: technologyCapabilityValidationRules.name,
  description: technologyCapabilityValidationRules.description,
  domainId: technologyCapabilityValidationRules.domainId,
  tags: z.array(z.string().min(1)).optional().default([]),
  notes: technologyCapabilityValidationRules.notes,
});

/**
 * TechnologyCapability update form schema (at least one field must be present)
 */
export const updateTechnologyCapabilitySchema = z
  .object({
    name: technologyCapabilityValidationRules.name.optional(),
    description: technologyCapabilityValidationRules.description,
    domainId: technologyCapabilityValidationRules.domainId,
    tags: z.array(z.string().min(1)).optional(),
    notes: technologyCapabilityValidationRules.notes,
  })
  .refine((data) => Object.values(data).some((value) => value !== undefined), {
    message: 'At least one field must be updated',
  });

/**
 * Type inference for create technology capability form
 */
export type CreateTechnologyCapabilityFormData = z.infer<typeof createTechnologyCapabilitySchema>;

/**
 * Type inference for update technology capability form
 */
export type UpdateTechnologyCapabilityFormData = z.infer<typeof updateTechnologyCapabilitySchema>;
